/**
 * HTTP layer for the SDK's single STOQ API call:
 *
 *   POST /api/v1/intents.json — create a back-in-stock signup.
 *
 * All read data comes from Shopify (shop metafields via the Storefront API,
 * or host-provided SSR data) — see ./metafields.ts. The backend identifies
 * the shop from the `X-Shopify-Shop-Domain` header.
 */
export const DEFAULT_HOST = 'https://app.stoqapp.com'

export interface ApiContext {
  host: string
  shop: string
  marketId?: number
  /** Normalized locale (lowercase, underscores), e.g. `en_us`. */
  locale?: string
}

export class StoqApiError extends Error {
  readonly status: number
  readonly endpoint: string

  constructor(endpoint: string, status: number, statusText: string) {
    super(`[Stoq] ${endpoint} responded ${status}${statusText ? ` ${statusText}` : ''}`)
    this.name = 'StoqApiError'
    this.status = status
    this.endpoint = endpoint
  }
}

export function buildHeaders(ctx: ApiContext): Record<string, string> {
  return {
    'X-Shopify-Shop-Domain': ctx.shop,
    'Content-Type': 'application/json',
    Accept: 'application/json',
  }
}

export interface IntentPayload {
  intent: Record<string, unknown>
  customer: Record<string, unknown>
  product?: Record<string, unknown>
}

export interface IntentResponse {
  status: number
  body: { intents?: unknown[]; errors?: unknown } | null
}

/**
 * POST /api/v1/intents.json — creates a back-in-stock signup.
 * The backend responds 201 with `{ intents, errors }` on success, and
 * 200 with `{ errors }` when validation fails (e.g. duplicate signup).
 */
export async function postIntent(ctx: ApiContext, payload: IntentPayload): Promise<IntentResponse> {
  const url = new URL('/api/v1/intents.json', ctx.host).toString()
  const response = await fetch(url, {
    method: 'POST',
    headers: buildHeaders(ctx),
    body: JSON.stringify(payload),
  })
  if (response.status >= 500) {
    throw new StoqApiError('/api/v1/intents.json', response.status, response.statusText)
  }
  let body: IntentResponse['body'] = null
  try {
    body = (await response.json()) as IntentResponse['body']
  } catch {
    body = null
  }
  return { status: response.status, body }
}
